import { PHASE_KEYS, TASK_PHASE_RULE, phaseIndex, type PhaseKey } from './phases.ts';
import { z } from 'zod';
import { PROCESS_KINDS } from './gates.ts';
import { SPECIAL_KEYS, SPECIAL_TASKS } from './specials.ts';

/**
 * The master process tree (tech-tree spec v3 §8): one tree for every kind of
 * job, built on the process tree page and locked in (`lockIn`), versioned
 * append-only per organization (process_versions). A job is the steps added
 * to it from the tree, each coming after the steps before it in the tree that
 * the job has; a job keeps its own copy of its steps, so a new version adds
 * no step to a running job, and only the steps that still follow the master
 * take what it changes (carryBack.ts). NSB's base tree (`BASE_TREE`) seeds an organization
 * that has none.
 *
 * A job starts on one node (`start`) and grows by hand from its phases' lists; a
 * package is a step with a trade, one performer's share of that trade.
 */

/** Who a step is on: NSB, the client, a sub, the engineer or architect, a supplier, or the authority (the building department's inspector). */
export const TASK_OWNERS = ['nsb', 'client', 'sub', 'arch', 'vendor', 'authority'] as const;
export type TaskOwner = (typeof TASK_OWNERS)[number];

/** A gate is a sign-off placed where the tree puts it: the software's by their keys, or the tree's own (the quote, the retainer). */
export const NODE_KINDS = ['work', 'milestone', 'decision', 'inspection', 'gate'] as const;
export type NodeKind = (typeof NODE_KINDS)[number];
/** What a job's step can be: the tree's kinds. */
export type StepKind = NodeKind;
/**
 * Whether a step of this kind holds a list of tasks: work, a decision or a sign-off (a gate); never a milestone or an
 * inspection. The one rule the tree's lists are read by (below), a job's are held by (apply.ts `checkHolder`) and a
 * job's panel offers its add lines by (slice 4's final review).
 */
export const holdsList = (kind: NodeKind): boolean => kind === 'work' || kind === 'decision' || kind === 'gate';

/**
 * Where a step of the master tree came from, in NSB's thinking: shown as a
 * colour and a word on the canvas while the tree is being reviewed against the
 * literature, so each step's provenance is plain.
 */
export const NODE_SOURCES = ['jason', 'spec', 'v07', 'fred', 'timber', 'renovation', 'jobs'] as const;
export type NodeSource = (typeof NODE_SOURCES)[number];
export const NODE_SOURCE_WORDS: Record<NodeSource, string> = {
  jason: 'NSB’s own process',
  spec: 'the process-flow spec',
  v07: 'the v0.7 task set',
  fred: 'Fred’s templates and tracker',
  timber: 'the timber-frame literature',
  renovation: 'the renovation literature',
  jobs: 'documented jobs',
};

const slug = z.string().regex(/^[a-z0-9][a-z0-9_.-]*$/, 'lowercase letters, digits, - _ .');
const text = z.string().trim().min(1);

/**
 * How a decision with options is decided (design study 22; Jason, 2026-10-07: "a decision for now is not a chose your
 * own adventure type thing, it litterally is a decision, either select one of many or many of many"): one of its
 * options chosen, or any number of them. A decision that says nothing is choose one.
 */
export const DECISION_CHOOSE = ['one', 'any'] as const;
export type DecisionChoose = (typeof DECISION_CHOOSE)[number];

/** One of a decision's options: its id, kept as the process tree page made it, and its words, NSB's for the client to choose from. */
export const TreeOptionDef = z.object({ id: z.string().regex(/^[a-z0-9]{1,12}$/), text: text.max(200) });
export type TreeOption = z.infer<typeof TreeOptionDef>;

export const ProcessNodeDef = z.object({
  key: slug,
  title: text,
  kind: z.enum(NODE_KINDS),
  /** Lives inside this step's own list (a step holds its tasks). */
  parent: slug.optional(),
  /** Comes before these, where the job has them: the tree's one link (a job comes after what it depends on). */
  before: z.array(slug).optional(),
  /** The kinds of project it belongs to; omitted for both. */
  for: z.enum(PROCESS_KINDS).optional(),
  /**
   * The phase it is drawn in (phases.ts). A task omitting it sits in its step's; a task's is its step's or an earlier
   * one, where it is done early (design study 31, slice 4), never a later one.
   */
  phase: z.enum(PHASE_KEYS).optional(),
  /** Inside a step: on with its parent, for these kinds of job. (At the top level a job starts on `start` alone.) */
  basic: z.array(z.enum(PROCESS_KINDS)).optional(),
  owner: z.enum(TASK_OWNERS).optional(),
  /** A package: one performer's share of this trade (a key from the org's trade list). */
  trade: slug.optional(),
  /** Who does it: the trades a step needs, so people are attached only where required. */
  trades: z.array(z.string().trim().min(1)).optional(),
  /** Ballpark calendar days, for planning and the critical path. */
  days: z.number().int().min(0).max(3650).optional(),
  /** Can be added many times, each with its own name: a second round of design. */
  repeatable: z.boolean().optional(),
  /**
   * A name of its own for the client, kept as stored versions have it: read by nothing, a step having one name for
   * everyone, its title (design study 31, slice 3). A stored version's landmark and landmark line are dropped as it is read.
   */
  clientLine: z.string().optional(),
  /** Why it matters, in a sentence: NSB's, shown with the step so the process is never a black box. */
  why: z.string().optional(),
  /**
   * What the client reads when they open the step (Jason, 2026-10-06: NSB's note and one for the client). None: the
   * client sees its tasks only, never NSB's note.
   */
  clientNote: z.string().optional(),
  notes: z.string().optional(),
  /** The steps that historically bite (spec §7). */
  flagged: z.boolean().optional(),
  /** Where it came from in NSB's thinking (NODE_SOURCES). */
  source: z.enum(NODE_SOURCES).optional(),
  /** Switches from the process tree, in words on the step: held by the permit, inspected by the authority, checked on site before it goes in. */
  permit: z.boolean().optional(),
  inspection: z.boolean().optional(),
  preInstall: z.boolean().optional(),
  /** It delivers a design review: added to a job, it opens a draft iteration for itself, and the client's review finishes it. */
  deliversReview: z.boolean().optional(),
  /**
   * It delivers an estimate (design study 17; Jason, 2026-10-07): on a job, NSB sets the job's price on it, a low and
   * a high with a note, and that is the funnel's band from that day until the next step that delivers one. Work or a
   * gate (the Quote the client approves); Discovery delivers the first by default.
   */
  deliversEstimate: z.boolean().optional(),
  /**
   * A decision's options, as the process tree page set them (design study 22): on a job, deciding it records which were
   * chosen. Never a branch: what comes after a decision comes after it whatever is chosen. A tree stored before has none.
   */
  options: z.array(TreeOptionDef).max(20).optional(),
  /** Whether one of its options is chosen, or any number of them; read as one when it says nothing. */
  choose: z.enum(DECISION_CHOOSE).optional(),
  /**
   * The special tasks it carries, from the library (`specials.ts`; spec "Pattern Mapping" §3): Pattern mapping on a step
   * of work. Omitted when it carries none; a tree stored before has none.
   */
  special: z.array(z.enum(SPECIAL_KEYS)).max(SPECIAL_KEYS.length).optional(),
});
export type ProcessNodeDefT = z.infer<typeof ProcessNodeDef>;

/** How a decision is decided: one of its options, or any of them (a decision that says nothing, one). */
export const chooseOf = (n: { choose?: DecisionChoose | undefined }): DecisionChoose => n.choose ?? 'one';

/**
 * A phase as the process tree page sets it, carried into the tree when it is
 * locked in (Jason, 2026-10-06, option A: "phases and what closes each come
 * from your tree"): its name and what happens in it, the step that closes it
 * on a job that has it (a job without it ends the phase with its last step in
 * it), and how sure the price is once it is closed.
 */
export const TreePhaseDef = z.object({
  key: z.enum(PHASE_KEYS),
  name: text,
  what: z.string(),
  closedBy: slug.nullable(),
  /** Of the price spread at the start, the share still open once it is closed: the funnel's narrowing. */
  sureAfter: z.number().min(0).max(1),
  /**
   * Its share of the time, a whole percentage, read as a weight among the phases still ahead (Jason, 2026-10-07: "do
   * it as percentages not actual times"; "On each transition, of the time left"): what places its closing gate on a
   * job's funnel while nobody has dated it. A tree stored before it has none.
   */
  share: z.number().int().min(0).max(100).optional(),
});
export type TreePhase = z.infer<typeof TreePhaseDef>;

/**
 * A tree as it is stored. A body written before the cleanup of 2026-10-07 also
 * carries what the software no longer reads (a step's estimate lines, package
 * decisions, old keys and the links of the tree before; the tree's
 * measurements and quantities): zod drops those keys on reading, and the next
 * lock-in writes a version without them.
 */
const TreeBody = z.object({
  key: slug,
  name: text,
  source: z.string(),
  /** The one step every job starts on. Omitted: the first top-level step. */
  start: slug.optional(),
  /**
   * A job is the steps it has (tech-tree spec v3 §8; Jason, 2026-10-06): every
   * step, a sign-off too, is added to a job by hand from what can come after
   * the steps it has, or left out of it, and a step comes after what it
   * depends on where the job has it. Nothing comes with a step but its own
   * list. Every tree the software reads is one (migrate refuses an
   * organization whose tree is not, cli/migrate.ts).
   */
  byHand: z.boolean().optional(),
  /** The process tree's draft version it was locked in from. */
  lockedFrom: z.number().int().positive().optional(),
  /** Its phases as the process tree page set them, in order. */
  phases: z.array(TreePhaseDef),
  nodes: z.array(ProcessNodeDef),
});
export type ProcessTreeDoc = z.infer<typeof TreeBody>;

export const ProcessTreeDocSchema = TreeBody.superRefine((doc, ctx) => {
  const issue = (path: (string | number)[], message: string) => ctx.addIssue({ code: 'custom', path, message });
  const byKey = new Map<string, ProcessNodeDefT>();
  doc.nodes.forEach((n, i) => {
    if (byKey.has(n.key)) issue(['nodes', i, 'key'], `Duplicate key ${n.key}`);
    byKey.set(n.key, n);
  });
  const phaseKeys = new Set<string>();
  doc.phases.forEach((p, i) => {
    if (phaseKeys.has(p.key)) issue(['phases', i, 'key'], `Two phases are ${p.key}`);
    phaseKeys.add(p.key);
    if (p.closedBy !== null && !byKey.has(p.closedBy)) issue(['phases', i, 'closedBy'], `${p.name} is closed by ${p.closedBy}, which the tree does not have`);
  });
  if (doc.start !== undefined) {
    const s = byKey.get(doc.start);
    if (!s) issue(['start'], `No step ${doc.start} to start on`);
    else if (s.parent !== undefined) issue(['start'], `${s.title} sits inside ${s.parent}: a job starts on a top-level step`);
    else if (s.for !== undefined) issue(['start'], `${s.title} is for one kind of job only: every job starts on the same step`);
    else if (s.repeatable) issue(['start'], `${s.title} is added by name: a job cannot start on it`);
  }
  doc.nodes.forEach((n, i) => {
    if (n.parent !== undefined) {
      const holder = byKey.get(n.parent);
      // Work holds steps, and a decision or a sign-off holds its own list too.
      if (!holder) issue(['nodes', i, 'parent'], `No step ${n.parent}`);
      else if (!holdsList(holder.kind)) issue(['nodes', i, 'parent'], `${n.parent} is a ${holder.kind}; it holds no steps`);
    }
    (n.before ?? []).forEach((k, j) => {
      if (k === n.key) issue(['nodes', i, 'before', j], `${n.key} cannot refer to itself`);
      else if (!byKey.has(k)) issue(['nodes', i, 'before', j], `No step ${k}`);
    });
    if (n.repeatable && n.kind !== 'work') issue(['nodes', i, 'repeatable'], 'Only work can be added many times');
    if (n.repeatable && n.parent) issue(['nodes', i, 'repeatable'], 'A repeatable step sits at the top level');
    if (n.trade !== undefined && n.kind !== 'work') issue(['nodes', i, 'trade'], 'Only a step of work is a package');
    if (n.deliversEstimate && n.kind !== 'work' && n.kind !== 'gate') issue(['nodes', i, 'deliversEstimate'], 'Only work or a gate delivers an estimate');
    if ((n.options !== undefined || n.choose !== undefined) && n.kind !== 'decision') issue(['nodes', i, 'options'], 'Only a decision has options to choose from');
    if (n.options && new Set(n.options.map((o) => o.id)).size !== n.options.length) issue(['nodes', i, 'options'], `${n.title} has two options of the same id`);
    const seenSpecial = new Set<string>();
    for (const k of n.special ?? []) {
      const s = SPECIAL_TASKS[k];
      if (seenSpecial.has(k)) issue(['nodes', i, 'special'], `${s.name} is on ${n.title} twice`);
      seenSpecial.add(k);
      if (!s.kinds.includes(n.kind)) issue(['nodes', i, 'special'], `${n.title} is ${n.kind}: only ${s.kinds.join(' or ')} carries ${s.name}`);
    }
    // A step at the top alone carries one (spec "Pattern Mapping" §3.3): only a step at the top has settings of its own on a job.
    if (n.special?.length && n.parent !== undefined) issue(['nodes', i, 'special'], `${n.title} is a task in ${n.parent}'s list: only a step at the top carries a special task`);
  });
  // No step inside itself; a task in its step's phase or an earlier one, its step the one at the top (design study 31,
  // slice 4), a phase left off read as its step's, as `phaseOfNode` reads it.
  doc.nodes.forEach((n, i) => {
    const seen = new Set<string>([n.key]);
    let top: ProcessNodeDefT | undefined;
    let own: PhaseKey | undefined = n.phase;
    for (let p = n.parent; p !== undefined; p = byKey.get(p)?.parent) {
      if (seen.has(p)) {
        issue(['nodes', i, 'parent'], `${n.key} sits inside itself`);
        return;
      }
      seen.add(p);
      top = byKey.get(p);
      own ??= top?.phase;
    }
    if (top?.phase !== undefined && own !== undefined && phaseIndex(own) > phaseIndex(top.phase)) issue(['nodes', i, 'phase'], `${n.title} is in a later phase than ${top.title}. ${TASK_PHASE_RULE}`);
  });
  // The order of work must run one way: what comes before what forms no loop.
  const next = new Map<string, string[]>();
  for (const n of doc.nodes) next.set(n.key, n.before ?? []);
  const state = new Map<string, 1 | 2>();
  const visit = (k: string, trail: string[]): boolean => {
    if (state.get(k) === 2) return false;
    if (state.get(k) === 1) {
      issue(['nodes'], `The order loops: ${[...trail.slice(trail.indexOf(k)), k].join(' → ')}`);
      return true;
    }
    state.set(k, 1);
    for (const m of next.get(k) ?? []) if (visit(m, [...trail, k])) return true;
    state.set(k, 2);
    return false;
  };
  for (const k of next.keys()) if (visit(k, [])) break;
});

export function parseProcessTree(raw: unknown): ProcessTreeDoc {
  return ProcessTreeDocSchema.parse(raw);
}

/** The step every job starts on: the doc's, else the first top-level step open to every kind of job. */
export function startKey(doc: ProcessTreeDoc): string | null {
  if (doc.start && doc.nodes.some((n) => n.key === doc.start)) return doc.start;
  return doc.nodes.find((n) => n.parent === undefined && n.for === undefined && !n.repeatable)?.key ?? null;
}

export function availableTo(n: ProcessNodeDefT, kind: (typeof PROCESS_KINDS)[number]): boolean {
  return n.for === undefined || n.for === kind;
}

/**
 * The kinds of step in words: a word before a mark (guide §20), but for a gate,
 * which is its mark alone (Jason, 2026-10-06: "just don't have anything for
 * gates, maybe just a gate icon").
 */
export const STEP_KIND_WORDS: Record<StepKind, string> = {
  work: 'Step',
  milestone: 'Milestone',
  decision: 'Client decision',
  inspection: 'Inspection',
  gate: '',
};
